import React, { useState, useEffect } from "react";
import { fetchModuleUsageDetail } from "../../../../services/adminMonitorService";
import { ENTITY_LABELS } from "../../../../utils/labelMapping";

const STATUS_COLORS = {
  RUNNING: { bg: "#e3f2fd", color: "#1565c0" },
  PENDING: { bg: "#fff3e0", color: "#e65100" },
  INITIATED: { bg: "#fff3e0", color: "#e65100" },
  RETRY: { bg: "#f3e5f5", color: "#6a1b9a" },
  COMPLETED: { bg: "#e8f5e9", color: "#1b5e20" },
  FAILED: { bg: "#ffebee", color: "#b71c1c" },
  INACTIVE: { bg: "#f5f5f5", color: "#424242" },
  REPLICATED: { bg: "#e1f5fe", color: "#01579b" },
};

const MODULE_LABEL = {
  discovery: "Discovery",
  get brandAudit() { return `Brand ${ENTITY_LABELS.audit}`; },
  categoryAnalysis: "Category Analysis",
};

function formatDate(dt) {
  if (!dt) return "-";
  return new Date(dt).toLocaleString();
}

function formatDuration(start, end) {
  if (!start) return "-";
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  const diff = Math.round((e - s) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ${diff % 60}s`;
  return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
}

function StatusChip({ status }) {
  const sc = STATUS_COLORS[status] || { bg: "#f5f5f5", color: "#555" };
  return (
    <span style={{
      background: sc.bg, color: sc.color,
      borderRadius: 10, padding: "2px 10px", fontSize: 11, fontWeight: 500,
    }}>
      {status}
    </span>
  );
}

export default function ModuleUsageDetailModal({ transactionId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchModuleUsageDetail(transactionId)
      .then(setDetail)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [transactionId]);

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e0e0e0", paddingBottom: 12, marginBottom: 16 }}>
          <div>
            <span style={{ fontWeight: 700, fontSize: 15, color: "#1a1a1a" }}>
              Transaction #{transactionId}
            </span>
            {detail && (
              <span style={{ marginLeft: 10, fontSize: 12, color: "#666" }}>
                {MODULE_LABEL[detail.request_type] || detail.request_type}
              </span>
            )}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#666", lineHeight: 1 }}>×</button>
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: 40, color: "#999" }}>Loading...</div>
        )}

        {!loading && detail && (
          <>
            {/* Meta info */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12, marginBottom: 20 }}>
              <MetaCard label="Status"><StatusChip status={detail.status} /></MetaCard>
              <MetaCard label="Customer">{detail.customer_name || "-"}</MetaCard>
              <MetaCard label="User">{detail.user_email || "-"}</MetaCard>
              <MetaCard label="Brand">{detail.brand_domain || "-"}</MetaCard>
              <MetaCard label="Run Type">{detail.run_type || "-"}</MetaCard>
              <MetaCard label="Started">{formatDate(detail.start_time)}</MetaCard>
              <MetaCard label="Ended">{formatDate(detail.end_time)}</MetaCard>
              <MetaCard label="Duration">{formatDuration(detail.start_time, detail.end_time)}</MetaCard>
            </div>

            {/* Progress summary */}
            {detail.requests?.length > 0 && (
              <ProgressSummary requests={detail.requests} />
            )}

            {/* Requests table */}
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#444", marginBottom: 8 }}>
                Requests ({detail.requests?.length || 0})
              </div>
              <div style={{ overflowX: "auto", maxHeight: 340, overflowY: "auto" }}>
                <table style={tableStyle}>
                  <thead style={{ position: "sticky", top: 0, background: "#f5f5f5" }}>
                    <tr>
                      {["#", "Brand", "Market", "LLM", "Persona", "Category", "Type", "Status", "Started", "Duration"].map((h) => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.requests.map((r, i) => (
                      <tr key={r.id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                        <td style={tdStyle}>{i + 1}</td>
                        <td style={tdStyle}>{r.brand || r.brand_domain || "-"}</td>
                        <td style={tdStyle}>{r.market || "-"}</td>
                        <td style={tdStyle}>{r.llm || "-"}</td>
                        <td style={tdStyle}>{r.persona || "-"}</td>
                        <td style={tdStyle}>{r.category || "-"}</td>
                        <td style={tdStyle}>{r.type || r.request_type || "-"}</td>
                        <td style={tdStyle}><StatusChip status={r.status} /></td>
                        <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{formatDate(r.request_start_time || r.created_at)}</td>
                        <td style={tdStyle}>{formatDuration(r.request_start_time, r.request_end_time)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {!loading && !detail && (
          <div style={{ textAlign: "center", padding: 40, color: "#999" }}>Transaction not found</div>
        )}
      </div>
    </div>
  );
}

function MetaCard({ label, children }) {
  return (
    <div style={{ background: "#fafafa", border: "1px solid #e8e8e8", borderRadius: 6, padding: "8px 12px" }}>
      <div style={{ fontSize: 10, color: "#999", fontWeight: 600, textTransform: "uppercase", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 12, color: "#222", fontWeight: 500 }}>{children}</div>
    </div>
  );
}

function ProgressSummary({ requests }) {
  const total = requests.length;
  const completed = requests.filter((r) => r.status === "COMPLETED").length;
  const failed = requests.filter((r) => r.status === "FAILED").length;
  const running = requests.filter((r) => ["RUNNING", "PENDING", "INITIATED", "RETRY"].includes(r.status)).length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div style={{ background: "#f9f9f9", border: "1px solid #e8e8e8", borderRadius: 6, padding: "10px 14px" }}>
      <div style={{ display: "flex", gap: 16, marginBottom: 8, fontSize: 12 }}>
        <span style={{ color: "#2e7d32" }}>✓ {completed} completed</span>
        {failed > 0 && <span style={{ color: "#d32f2f" }}>✗ {failed} failed</span>}
        {running > 0 && <span style={{ color: "#1565c0" }}>⟳ {running} active</span>}
        <span style={{ color: "#999" }}>/ {total} total</span>
      </div>
      <div style={{ background: "#e0e0e0", borderRadius: 4, height: 6, overflow: "hidden" }}>
        <div style={{ background: "#2e7d32", width: `${pct}%`, height: "100%", transition: "width 0.3s" }} />
      </div>
      <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>{pct}% complete</div>
    </div>
  );
}

const overlayStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1300, padding: 16,
};
const modalStyle = {
  background: "#fff", borderRadius: 10, padding: 24,
  width: "100%", maxWidth: 900, maxHeight: "90vh",
  overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
};
const tableStyle = {
  width: "100%", borderCollapse: "collapse", fontSize: 11,
  border: "1px solid #e8e8e8",
};
const thStyle = {
  padding: "6px 8px", textAlign: "left", fontSize: 10,
  fontWeight: 600, color: "#555", borderBottom: "1px solid #e0e0e0",
  whiteSpace: "nowrap",
};
const tdStyle = { padding: "6px 8px", verticalAlign: "middle", fontSize: 11 };
